import { useEffect, useState } from 'react';
import {
    clickLngLatAtom,
    dataLoadedResultAtom,
    fiwareFetchStatusesAtom,
    gyoseikaiObjAtom,
    menuItemsAtom
} from '../../atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { LAYER_GEOMETRY_TYPES } from '../../lib/smartCityStyle';
import { mapType, nonLngLatDataType } from '../../types';
import { useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { getFiwareErrorMessages } from '../../lib/getFiwareErrMsg';

export const useToggleLayerControl = (
    map: maplibregl.Map | null, 
    selectedLayers: string[] | undefined, 
    mapType: mapType,
    setErrorMessage: (value: string[] | undefined) => void,
    nonLngLatDataArr: nonLngLatDataType[] | undefined,
    setNonLngLatDataArr: (value: nonLngLatDataType[] | undefined) => void,
    setClickEvent: (clickEvent: maplibregl.MapMouseEvent | undefined) => void
) => {
    const menuItems = useAtomValue(menuItemsAtom);
    const gyoseikaiObj = useAtomValue(gyoseikaiObjAtom);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLayerToggleComplete, setIsLayerToggleComplete] = useState<boolean>(false);
    const setLngLat = useSetAtom(clickLngLatAtom);
    const dataLoadedResult = useAtomValue(dataLoadedResultAtom);
    const fiwareFetchStatuses = useAtomValue(fiwareFetchStatusesAtom);

    /* ****************
     * 切り替え処理
     * ****************/
    useEffect(() => {
        if (
            !map || !selectedLayers || !menuItems ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !dataLoadedResult || (dataLoadedResult as any).result !== 'success'
        ) {
            return;
        }

        const targetLayers = selectedLayers;
        const errorMsgSet = new Set<string>();
        const newNonLngLatDataArr: nonLngLatDataType[] = nonLngLatDataArr ? [...nonLngLatDataArr] : [];

        const handleStyleData = async () => {
            const promises = menuItems.map((item) => {
                return new Promise<void>((resolve) => {
                    const isSelected = targetLayers.find((layer) => layer === item.id);
                    const geometryType = item.metadata?.dataType;

                    // アラート表示の場合、パネルを表示
                    if (geometryType === 'Alert') {
                        const nonLngLatDataIndex = newNonLngLatDataArr.findIndex((data) => data.title === item.id);
                        if (nonLngLatDataIndex == -1  && isSelected) {
                            const fiwareFetchStatus = item.dataType === 'fiware' ? fiwareFetchStatuses[item.dataId || ''] : undefined;
                            if (fiwareFetchStatus !== undefined && !fiwareFetchStatus) {
                                errorMsgSet.add(`${item.id} のデータ連携は停止しています`);
                            } else {
                                errorMsgSet.add(`${item.id} のデータはありません`);
                            }
                        } else {
                            const updatedNonLngLatData = { ...newNonLngLatDataArr[nonLngLatDataIndex], isShow: !!isSelected };
                            newNonLngLatDataArr[nonLngLatDataIndex] = updatedNonLngLatData;
                            if(nonLngLatDataArr && JSON.stringify(newNonLngLatDataArr[nonLngLatDataIndex]) !== JSON.stringify(nonLngLatDataArr[nonLngLatDataIndex])) {
                                setNonLngLatDataArr(newNonLngLatDataArr);
                            }
                        }
                        resolve();
                    }

                    const addedLayers: string[] = [];
                    let isNewLayers = false;

                    // 切り替えるレイヤーを取得
                    const layerIds = (item.dataType === 'osm' && item.layerIds) ?
                        item.layerIds
                        :
                        Object.keys(LAYER_GEOMETRY_TYPES).map((key) =>
                            `${item.id}-${LAYER_GEOMETRY_TYPES[key as keyof typeof LAYER_GEOMETRY_TYPES]}`
                        );

                    let noneLayerCount = 0;

                    layerIds.forEach((layerId) => {
                        // 表示
                        if (isSelected) {
                            if (map.getLayer(layerId)) {
                                const visibility = map.getLayoutProperty(layerId, 'visibility');
                                // レイヤーが非表示の場合のみ、表示に変更
                                if (visibility === 'none') {
                                    map.setLayoutProperty(layerId, 'visibility', 'visible');
                                    isNewLayers = true;
                                }
                                addedLayers.push(layerId);
                            } else {
                                noneLayerCount++;
                            }
                        } else {
                            if (map.getLayer(layerId)) {
                                map.setLayoutProperty(layerId, 'visibility', 'none');
                            }
                        }
                    });

                    // 全てのレイヤーでフィーチャーが表示されていない場合にエラーメッセージを表示
                    if (isSelected && (layerIds.length === noneLayerCount || (item.metadata?.dataType !== 'datapng' && item.metadata?.dataType !== 'raster' && item.id !== gyoseikaiObj?.id))) {
                        map.once('idle', () => {
                            if (!isNewLayers) {
                                resolve();
                                return;
                            }
                            const features = map.queryRenderedFeatures({ layers: addedLayers });
                            const errorMsgs = getFiwareErrorMessages(features, item, fiwareFetchStatuses);
                            errorMsgs.forEach(msg => errorMsgSet.add(msg));
                            
                            resolve();
                        });
                    } else {
                        resolve();
                    }
                });
            });

            await Promise.all(promises);

            if (errorMsgSet.size > 0) {
                setErrorMessage(Array.from(errorMsgSet));
            }

            setIsLayerToggleComplete(true);
        };

        handleStyleData();

    }, [map, mapType, menuItems, selectedLayers, gyoseikaiObj, nonLngLatDataArr]);

    // マーカーがある場合、クリックイベントを発火
    useEffect(() => {
        if(!isLayerToggleComplete) { return; }

        const initialMarker = searchParams.get('marker');
        const initialCenter = map?.getCenter();

        if(!initialMarker || !map || !initialCenter) { return; }

        setClickEvent(
          {
            type: 'click',
            lngLat: initialCenter,
            point: map.project(initialCenter as maplibregl.LngLatLike),
            target: map,
            originalEvent: new MouseEvent('click')
          } as unknown as maplibregl.MapMouseEvent,
        );

        setLngLat([initialCenter.lng, initialCenter.lat]);

        //【GoogleAnalytics】URLシェア トラッキング
        ReactGA.event({
            category: "url_share",
            action: "share",
            label: window.location.href
        });

        setSearchParams((prev) => {
            prev.delete('marker');
            return prev;
        });

    }, [isLayerToggleComplete]);
};